/**
 * The inbox's dry vocabulary (M14, FR-6). Pure copy and pure derivations — how a
 * band reads, how a percentile is spoken, how the receipts line is joined, and
 * the humanised reason chips — so every judgement is testable on its own and the
 * card itself stays a render of what it is handed.
 */
import type {
  InboxBand,
  InboxDismissChip,
  InboxReasonChip,
  InboxReceipts,
  InboxView,
  KnockGate,
  NobCall,
} from "../../lib/api";
// The bare stop, from the layer that also puts it under a name in the list —
// one literal, so a row and the card it opens cannot say it two ways.
import { GUARD_CLOSED_PREVIEW } from "../../lib/conversations";

/** The band pill's colour: TOP_5 amber, TOP_25 lighter, MIDDLE zinc, BOTTOM dim. */
export const BAND_PILL: Record<InboxBand, string> = {
  TOP_5: "border-accent/60 bg-accent/10 text-accent-ink-strong",
  TOP_25: "border-accent/25 bg-accent/[0.04] text-accent-ink-strong/80",
  MIDDLE: "border-border-strong text-text-soft",
  BOTTOM_25: "border-border text-faint",
  BOTTOM_5: "border-border text-fainter",
  BOTTOM: "border-border text-faint",
  TIER_STRONG: "border-accent/60 bg-accent/10 text-accent-ink-strong",
  TIER_REASONABLE: "border-border-strong text-text-soft",
  TIER_WEAK: "border-border text-faint",
};

/**
 * The band as one plain word — the triage row's first column.
 *
 * THE COLD-START TIERS SAY WHAT THEY ARE. A door under `MIN_RANK_SAMPLE` has
 * no window to rank within, so the server sends an absolute tier off the raw
 * score; the word is the tier's own ("A strong knock", "A weak knock" in
 * `bands.ts`), never a percentile band's, because a rank is exactly what this
 * door cannot claim. The Rank row beside it already says "Too few knocks here
 * to rank", which is the other half of the same honesty.
 */
export const BAND_WORD: Record<InboxBand, string> = {
  TOP_5: "Top 5",
  TOP_25: "Top 25",
  MIDDLE: "Middle",
  BOTTOM_25: "Low",
  BOTTOM_5: "Low",
  BOTTOM: "Low",
  TIER_STRONG: "Strong",
  TIER_REASONABLE: "Reasonable",
  TIER_WEAK: "Weak",
};

export const VIEW_LABEL: Record<InboxView, string> = {
  top: "Top",
  recent: "Recent",
  saved: "Saved",
  actioned: "Actioned",
  everything: "Everything",
};

/**
 * The percentile as a plain rank the recipient is allowed to read: a high
 * percentile is a high standing, so "96" reads as "Top 4%". Below the median it
 * says so rather than flattering — "Bottom 20%".
 */
/**
 * A rank the door can actually support, or null. Below MIN_RANK_SAMPLE (20
 * scored knocks) the server sends `percentile: null` — coercing that to 0 and
 * rendering "Bottom 1%" states a confident rank the door has no basis for.
 */
export function percentileLabel(percentile: number | null | undefined): string | null {
  if (percentile == null || !Number.isFinite(percentile)) return null;
  const top = Math.round(100 - percentile);
  if (top <= 50) return `Top ${Math.max(1, top)}%`;
  return `Bottom ${Math.max(1, Math.round(percentile))}%`;
}

/** What a card shows when the door cannot rank yet. */
export const UNRANKED_LABEL = "Too few knocks here to rank";

/** "1 page", "6 pages" — a count and its noun, agreeing (wave 83: "1 sources"). */
export function counted(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/**
 * How many claims a check ran on. The drill-down is told (`checked_claims`);
 * a list row is not, and there verified and refuted are the floor of it — a
 * claim that was confirmed or contradicted was certainly checked.
 */
function checkedOf(r: InboxReceipts): number {
  return Math.max(r.checked_claims ?? 0, r.verified_claims + r.refuted_claims);
}

/**
 * THE CLAIMS, IN ONE VOCABULARY (wave 83): checked, verified, refuted.
 *
 * "✓ 0 of 1 claims" sat beside "1 refuted", and "0 verified · 0 refuted" sat
 * beside a knocker told "6 claims found" — which were six pages. So the words
 * are the same three everywhere, a count is said only where it is not zero,
 * and a check mark rides on a verified claim and on nothing else.
 */
export function claimsLine(r: InboxReceipts): string {
  const checked = checkedOf(r);
  if (checked === 0) return "None checked";
  const parts = [`${checked} checked`];
  if (r.verified_claims > 0) parts.push(`✓ ${r.verified_claims} verified`);
  if (r.refuted_claims > 0) parts.push(`${r.refuted_claims} refuted`);
  return parts.join(" · ");
}

/**
 * "3 claims checked · ✓ 1 verified · 6 pages read · 2 sources" — the receipt,
 * in one line (FR-6.3). Only work that HAPPENED is on it: a knock the doorman
 * checked nothing for earns an empty string, not a row of zeros — a receipt
 * itemising nothing is noise wearing a checkmark.
 */
export function receiptsLine(r: InboxReceipts): string {
  const checked = checkedOf(r);
  const parts: string[] = [];
  if (checked > 0) parts.push(`${counted(checked, "claim")} checked`);
  if (r.verified_claims > 0) parts.push(`✓ ${r.verified_claims} verified`);
  if (r.refuted_claims > 0) parts.push(`${r.refuted_claims} refuted`);
  if (r.pages_reviewed > 0) parts.push(`${counted(r.pages_reviewed, "page")} read`);
  if (r.sources_checked > 0) parts.push(counted(r.sources_checked, "source"));
  return parts.join(" · ");
}

/**
 * How long the account has been on X, as a person would say it: "4 yrs on X",
 * "7 mths on X", "12 days on X". Years from a full year up, months from a
 * full month up, days below that — the unit that makes the number honest, and
 * a young account reads as young at a glance (owner, 2026-09-10).
 */
export function accountAge(days: number): string {
  if (days >= 365) {
    const y = Math.floor(days / 365);
    return `${y} ${y === 1 ? "yr" : "yrs"} on X`;
  }
  if (days >= 30) {
    const m = Math.floor(days / 30);
    return `${m} ${m === 1 ? "mth" : "mths"} on X`;
  }
  return `${days} ${days === 1 ? "day" : "days"} on X`;
}

/**
 * The sender's under-line: "2 yrs on X · 12,300 followers · vector.co". The X
 * facts appear only when there is an X identity behind them — null is "no
 * account to measure", and "0 days on X · 0 followers" was that null dressed
 * up as the world's least credible sender.
 */
export function senderLine(
  accountAgeDays: number | null,
  followers: number | null,
  companyUrl: string | null,
): string {
  const parts: string[] = [];
  if (accountAgeDays !== null) parts.push(accountAge(accountAgeDays));
  if (followers !== null) parts.push(`${groupThousands(followers)} followers`);
  const domain = domainOf(companyUrl);
  if (domain) parts.push(domain);
  return parts.join(" · ");
}

/**
 * Who the card names, with what exists: the X handle, else the display name,
 * else the honest fact that this sender reached the door by email. Never a bare
 * "@" — an at-sign with nobody after it is a rendering of the missing value,
 * not of the sender.
 */
export function senderName(handle: string, displayName: string | null): string {
  if (handle) return `@${handle}`;
  if (displayName) return displayName;
  return "Email sender";
}

/** The host of a company URL, tidied: "https://www.vector.co/x" → "vector.co". */
export function domainOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    const match = /([a-z0-9.-]+\.[a-z]{2,})/i.exec(url);
    return match?.[1] ? match[1].replace(/^www\./, "") : null;
  }
}

/** Grouped by hand, so a follower count reads identically in a test and a browser. */
export function groupThousands(n: number): string {
  const whole = Math.round(Number.isFinite(n) ? n : 0);
  return Math.abs(whole)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * The RANKING chips (FR-6.4, FR-8.2) — the answers to "what was off about the
 * ranking?". `shouldnt_surface` is the one with a consequence: the server
 * demotes the knock on the strength of it.
 *
 * These are no longer the dismissal's chips. One list served both questions and
 * the two questions are not the same one: this grades Nob's work, and "Not for
 * me" says what the owner made of the person (owner item 10, 2026-08-26).
 */
export const REASON_CHIPS: readonly InboxReasonChip[] = [
  "shouldnt_surface",
  "ranked_too_low",
  "summary_wrong",
  "wrong_category",
  "good_sender_bad_timing",
  "other",
];

export const REASON_LABEL: Record<InboxReasonChip, string> = {
  shouldnt_surface: "Shouldn’t have surfaced",
  ranked_too_low: "Ranked too low",
  summary_wrong: "Summary is wrong",
  wrong_category: "Wrong category",
  good_sender_bad_timing: "Good sender, bad timing",
  other: "Something else",
};

/** The DISMISSAL chips — the answers to "why is it not for you?" (owner item 10). */
export const DISMISS_CHIPS: readonly InboxDismissChip[] = [
  "shouldnt_passed",
  "not_interested",
  "not_enough_evidence",
  "spam_scam",
  "good_sender_bad_timing",
  "other",
];

export const DISMISS_LABEL: Record<InboxDismissChip, string> = {
  shouldnt_passed: "Shouldn’t have passed",
  not_interested: "Not interested",
  not_enough_evidence: "Not enough evidence",
  spam_scam: "Spam or scam",
  good_sender_bad_timing: "Good sender, bad timing",
  other: "Other",
};

/**
 * WHICH CHIPS THIS KNOCK MAY BE DISMISSED WITH.
 *
 * "Shouldn't have passed" is a complaint about the bar, and it is only sayable
 * about a knock that got over it — either on its own merits (`cleared_bar`) or
 * because the owner reached in and saved it. Offering it on a knock that never
 * passed invites an answer to a question nobody asked, and records a signal
 * that means nothing.
 */
export function dismissChipsFor(knock: {
  standing: { cleared_bar: boolean };
  saved: boolean;
}): readonly InboxDismissChip[] {
  const passed = knock.standing.cleared_bar || knock.saved;
  return passed ? DISMISS_CHIPS : DISMISS_CHIPS.filter((chip) => chip !== "shouldnt_passed");
}

/**
 * WHY THE GUARD STOPPED, IN THE PRODUCT'S REGISTER (§8).
 *
 * `reason_code` is a machine word — the server writes `SUSPECT_INPUT` or
 * `HARMFUL_CONTENT` on the row — and a machine word on a card is the product
 * showing its plumbing to somebody deciding about a person. These are the two
 * sentences it becomes.
 *
 * A code this file has no sentence for falls back to the bare stop rather than
 * being printed: an unrecognised word tells the reader nothing they can act on
 * and everything about the schema. THE CODE IS NEVER RENDERED, and the leak
 * test for that lives beside these.
 *
 * The stop itself is not a judgement (§8): the gate did not move, the doorman
 * simply stopped listening, and every verb is still on the card underneath.
 * None of these sentences says anything about the person.
 */
export { GUARD_CLOSED_PREVIEW as GUARD_STOPPED };

const GUARD_REASON: Record<string, string> = {
  SUSPECT_INPUT: "The doorman stopped this hearing: the messages read as attempts to steer it.",
  HARMFUL_CONTENT: "The doorman stopped this hearing: the content was harmful.",
};

export function guardClosedLine(reason: string | null): string {
  return (reason && GUARD_REASON[reason]) || GUARD_CLOSED_PREVIEW;
}

/** The placeholder over the one line behind "Other". Nob reads it; nobody else does. */
export const DISMISS_OTHER_PLACEHOLDER = "In your own words (optional)";
export const DISMISS_OTHER_NOTE = "Only Nob reads this.";
export const DISMISS_OTHER_SEND = "Send";
/** The one line's worth the server will take (`packages/schemas` DISMISS_NOTE_MAX). */
export const DISMISS_NOTE_MAX = 280;

/**
 * WHAT WAS DECIDED ABOUT THIS KNOCK, or null while nothing has been.
 *
 * The three themes each carried the verdict differently — the skin's stamp said
 * the BAND on everything, so a knock the owner had answered still read
 * "Middle" — and the fix is one derivation all of them read (owner item 9).
 *
 * IT IS THE GATE, AND THE GATE HOLDS ONE VALUE (`docs/conversation-model.md`
 * §3), so there is nothing left to order: the recipient's last act IS the
 * field. An owner who let somebody in and later declined them has a `declined`
 * row and this reads "Turned away", exactly as the desk's own Stage row says
 * one line below. Two readings of one fact, disagreeing on the same card, is
 * the thing this function exists to stop.
 *
 * A Save is not a gate move and is read underneath it: it is the owner
 * overriding a bar, not deciding about a person.
 */
export type Decision = { word: string; tone: "ok" | "bad" };

export function decisionOf(knock: { gate: KnockGate; saved: boolean }): Decision | null {
  if (knock.gate === "declined" || knock.gate === "blocked") {
    return { word: "Turned away", tone: "bad" };
  }
  if (knock.gate === "let_in") return { word: "Let through", tone: "ok" };
  if (knock.saved) return { word: "Saved", tone: "ok" };
  return null;
}

/** The card pill's colour when it carries a decision rather than a band. */
export const DECISION_PILL: Record<Decision["tone"], string> = {
  ok: "border-accent/60 bg-accent/10 text-accent-ink-strong",
  bad: "border-border-strong text-text-soft",
};

/**
 * NOB'S CALL, IN THE OWNER'S WORDS (ruling R-0925-1 (2)).
 *
 * A knock Nob recommended keeps "Open the door" as its main action and needs
 * no sentence. One it did not recommend says so, once, with the reason in a
 * line — from the server's own code, never the reader's guess — and its
 * action goes quiet without going away: the owner can open any knock.
 *
 * OWNER COPY ONLY. The sender's side of the wire carries no call and no
 * reason, and nothing here is imported by the knocker's surfaces.
 */
export const NOT_RECOMMENDED = "Nob didn't recommend this";

/** The guard's two stops, named — the abusive one above all (R-0925-1 (2)). */
const STOPPED_REASON: Record<string, string> = {
  HARMFUL_CONTENT: "Stopped for abusive or harmful messages.",
  SUSPECT_INPUT: "Stopped for trying to steer the doorman.",
};

export function nobCallReason(call: NobCall, guardReason: string | null): string {
  switch (call.reason) {
    case "stopped":
      return (guardReason && STOPPED_REASON[guardReason]) || "Stopped by the doorman.";
    case "door_closed":
      return call.line
        ? `Your rules let no one through: “${call.line}”`
        : "Your rules let no one through.";
    case "minor":
      return "They said they are under 18.";
    case "strict_receipt":
      return "Your door is strict, and a receipt was refused or could not be checked.";
    case "strict_story_changed":
      return "Your door is strict, and their story changed when it was questioned.";
    case "exclusion":
      return call.line
        ? `Your rules exclude this: “${call.line}”`
        : "It matches a line under What doesn’t.";
    case "procedure_refuse":
      return call.line
        ? `Your rules turn this kind of knock away: “${call.line}”`
        : "Your rules turn this kind of knock away.";
    default:
      return "Below the bar your rules set.";
  }
}

/** Was this knock NOT recommended? False while Nob has made no call (§6.2). */
export function notRecommended(knock: { nob_call: NobCall | null }): boolean {
  return knock.nob_call !== null && !knock.nob_call.recommended;
}
